import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiArrowLeft, FiEdit, FiDownload, FiMail, FiCreditCard, FiCheck, 
  FiX, FiClock, FiUser, FiCalendar, FiFileText, FiPackage, FiPercent,
  FiTrash2, FiCopy, FiShare2, FiEye, FiEyeOff
} from 'react-icons/fi';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'react-toastify';
import { Quote, Invoice } from '../../types';
import { ConvertQuoteModal } from '../../components/quotes/ConvertQuoteModal';
import { useCurrency } from '../../contexts/CurrencyContext';
import { downloadQuotePDF } from '../../utils/quotePdfGenerator';
import styles from './QuoteView.module.css';

const mockQuote: Quote = {
  id: '1',
  quoteNumber: 'Q-2024-001',
  clientId: '1',
  client: {
    id: '1',
    name: 'TechCorp Solutions',
    email: 'contact@techcorp.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Business Ave',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94105',
      country: 'USA'
    },
    paymentTerms: 'net30',
    tags: ['technology', 'enterprise'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-15')
  },
  items: [
    {
      id: '1',
      description: 'Web Development Services - Custom e-commerce platform with modern UI/UX design',
      quantity: 1,
      unitPrice: 5000,
      taxRate: 10,
      taxAmount: 500,
      total: 5500
    },
    {
      id: '2',
      description: 'UI/UX Design - Complete design system and user experience optimization',
      quantity: 1,
      unitPrice: 2500,
      taxRate: 10,
      taxAmount: 250,
      total: 2750
    },
    {
      id: '3',
      description: 'Consulting Services - Technical architecture and project planning',
      quantity: 20,
      unitPrice: 150,
      taxRate: 10,
      taxAmount: 300,
      total: 3300
    }
  ],
  subtotal: 10500,
  taxAmount: 1050,
  discountAmount: 500,
  total: 11050,
  status: 'sent',
  issueDate: new Date('2024-01-15'),
  validUntil: new Date('2024-02-15'),
  notes: 'This quote includes comprehensive web development services with modern design principles. We will provide ongoing support and maintenance for the first 3 months after project completion.',
  terms: 'Payment terms: 50% upfront, 50% upon project completion. Project timeline: 8-12 weeks. All work includes 3 months of post-launch support and bug fixes.',
  createdBy: 'John Doe',
  createdAt: new Date('2024-01-15'),
  updatedAt: new Date('2024-01-15')
};

export function QuoteView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatCurrencySimple } = useCurrency();
  const [quote] = useState<Quote>(mockQuote);
  const [showActions, setShowActions] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isExpired = isBefore(quote.validUntil, new Date());
  const daysUntilExpiry = Math.ceil((quote.validUntil.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'var(--gray-600)', bg: 'var(--gray-100)', icon: <FiFileText size={16} /> },
      sent: { color: 'var(--blue-600)', bg: 'var(--blue-100)', icon: <FiMail size={16} /> },
      accepted: { color: 'var(--green-600)', bg: 'var(--green-100)', icon: <FiCheck size={16} /> },
      rejected: { color: 'var(--red-600)', bg: 'var(--red-100)', icon: <FiX size={16} /> },
      expired: { color: 'var(--orange-600)', bg: 'var(--orange-100)', icon: <FiClock size={16} /> }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <span className={styles.statusBadge} style={{ color: config.color, backgroundColor: config.bg }}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleAcceptQuote = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Quote accepted successfully!');
      // In a real app, this would update the quote status and create an invoice
    } catch (error) {
      toast.error('Failed to accept quote');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectQuote = async () => {
    if (window.confirm('Are you sure you want to reject this quote?')) {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success('Quote rejected');
        // In a real app, this would update the quote status
      } catch (error) {
        toast.error('Failed to reject quote');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleConvertToInvoice = () => {
    setShowConvertModal(true);
  };

  const handleConvertQuote = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      // In a real app, this would save the invoice to the database
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to the new invoice
      navigate(`/invoices/${invoice.id}`);
      toast.success('Quote converted to invoice successfully!');
    } catch (error) {
      toast.error('Failed to convert quote to invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendQuote = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Quote sent successfully!');
    } catch (error) {
      toast.error('Failed to send quote');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsLoading(true);
      
      // Prepare data for PDF generation
      const pdfData = {
        quoteNumber: quote.quoteNumber,
        issueDate: quote.issueDate,
        validUntil: quote.validUntil,
        acceptedDate: quote.acceptedDate,
        client: {
          name: quote.client.name,
          email: quote.client.email,
          phone: quote.client.phone,
          address: quote.client.address
        },
        items: quote.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          total: item.total
        })),
        subtotal: quote.subtotal,
        taxAmount: quote.taxAmount,
        discountAmount: quote.discountAmount,
        total: quote.total,
        notes: quote.notes,
        terms: quote.terms,
        status: quote.status
      };

      await downloadQuotePDF(pdfData);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleDeleteQuote = () => {
    if (window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      toast.success('Quote deleted successfully!');
      navigate('/quotes');
    }
  };

  return (
    <motion.div 
      className={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/quotes" className={styles.backButton}>
            <FiArrowLeft size={16} />
            Back to Quotes
          </Link>
          <div className={styles.quoteInfo}>
            <h1 className={styles.title}>{quote.quoteNumber}</h1>
            <p className={styles.subtitle}>
              Created for {quote.client.name} • {format(quote.issueDate, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>
        
        <div className={styles.headerActions}>
          <div className={styles.statusSection}>
            {getStatusBadge(quote.status)}
            {quote.status === 'sent' && !isExpired && (
              <span className={styles.expiryInfo}>
                {daysUntilExpiry} days left
              </span>
            )}
            {isExpired && quote.status === 'sent' && (
              <span className={styles.expiredInfo}>
                Expired {Math.abs(daysUntilExpiry)} days ago
              </span>
            )}
          </div>
          
          <div className={styles.actionButtons}>
            <button 
              onClick={() => setShowActions(!showActions)}
              className={styles.moreButton}
            >
              {showActions ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              Actions
            </button>
            
            <AnimatePresence>
              {showActions && (
                <motion.div 
                  className={styles.actionsDropdown}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link to={`/quotes/${id}/edit`} className={styles.actionItem}>
                    <FiEdit size={16} />
                    Edit Quote
                  </Link>
                  
                  <button 
                    onClick={handleDownloadPDF} 
                    className={styles.actionItem}
                    disabled={isLoading}
                  >
                    <FiDownload size={16} />
                    {isLoading ? 'Generating...' : 'Download PDF'}
                  </button>
                  
                  <button onClick={handleSendQuote} className={styles.actionItem}>
                    <FiMail size={16} />
                    Send Quote
                  </button>
                  
                  {quote.status === 'accepted' && (
                    <button onClick={handleConvertToInvoice} className={styles.actionItem}>
                      <FiCreditCard size={16} />
                      Convert to Invoice
                    </button>
                  )}
                  
                  <button onClick={handleCopyLink} className={styles.actionItem}>
                    <FiCopy size={16} />
                    Copy Link
                  </button>
                  
                  <button onClick={handleDeleteQuote} className={`${styles.actionItem} ${styles.deleteAction}`}>
                    <FiTrash2 size={16} />
                    Delete Quote
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Quote Details */}
        <div className={styles.mainSection}>
          <div className={styles.quoteCard}>
            <div className={styles.quoteHeader}>
              <div className={styles.companyInfo}>
                <h2>C-Band</h2>
                <p>Professional Invoicing & Accounting</p>
                <p>info@c-band.com</p>
                <p>+1 (555) 987-6543</p>
              </div>
              
              <div className={styles.quoteDetails}>
                <h1>QUOTE</h1>
                <div className={styles.detailRow}>
                  <span>Quote Number:</span>
                  <span>{quote.quoteNumber}</span>
                </div>
                <div className={styles.detailRow}>
                  <span>Issue Date:</span>
                  <span>{format(quote.issueDate, 'MMM dd, yyyy')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span>Valid Until:</span>
                  <span>{format(quote.validUntil, 'MMM dd, yyyy')}</span>
                </div>
                {quote.acceptedDate && (
                  <div className={styles.detailRow}>
                    <span>Accepted Date:</span>
                    <span>{format(quote.acceptedDate, 'MMM dd, yyyy')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Client Information */}
            <div className={styles.clientSection}>
              <h3>Bill To:</h3>
              <div className={styles.clientInfo}>
                <h4>{quote.client.name}</h4>
                <p>{quote.client.email}</p>
                {quote.client.phone && <p>{quote.client.phone}</p>}
                {quote.client.address && (
                  <div className={styles.address}>
                    <p>{quote.client.address.street}</p>
                    <p>{quote.client.address.city}, {quote.client.address.state} {quote.client.address.zipCode}</p>
                    <p>{quote.client.address.country}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className={styles.itemsSection}>
              <table className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Tax Rate</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((item, index) => (
                    <tr key={item.id}>
                      <td className={styles.itemDescription}>
                        <div>
                          <strong>{item.description}</strong>
                        </div>
                      </td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrencySimple(item.unitPrice)}</td>
                      <td>{item.taxRate}%</td>
                      <td>{formatCurrencySimple(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className={styles.totalsSection}>
              <div className={styles.totalRow}>
                        <span>Subtotal:</span>
        <span>{formatCurrencySimple(quote.subtotal)}</span>
              </div>
              {quote.discountAmount > 0 && (
                <div className={styles.totalRow}>
                          <span>Discount:</span>
        <span>-{formatCurrencySimple(quote.discountAmount)}</span>
                </div>
              )}
              <div className={styles.totalRow}>
                <span>Tax:</span>
                <span>{formatCurrencySimple(quote.taxAmount)}</span>
              </div>
              <div className={styles.totalRow}>
                <strong>Total:</strong>
                <strong>{formatCurrencySimple(quote.total)}</strong>
              </div>
            </div>

            {/* Notes and Terms */}
            {quote.notes && (
              <div className={styles.notesSection}>
                <h3>Notes:</h3>
                <p>{quote.notes}</p>
              </div>
            )}

            {quote.terms && (
              <div className={styles.termsSection}>
                <h3>Terms & Conditions:</h3>
                <p>{quote.terms}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>
          {/* Quick Actions */}
          <div className={styles.sidebarCard}>
            <h3>Quick Actions</h3>
            <div className={styles.quickActions}>
              {quote.status === 'sent' && !isExpired && (
                <>
                  <button 
                    onClick={handleAcceptQuote}
                    disabled={isLoading}
                    className={`${styles.actionButton} ${styles.acceptButton}`}
                  >
                    <FiCheck size={16} />
                    Accept Quote
                  </button>
                  <button 
                    onClick={handleRejectQuote}
                    disabled={isLoading}
                    className={`${styles.actionButton} ${styles.rejectButton}`}
                  >
                    <FiX size={16} />
                    Reject Quote
                  </button>
                </>
              )}
              
              {quote.status === 'accepted' && (
                <button 
                  onClick={handleConvertToInvoice}
                  disabled={isLoading}
                  className={`${styles.actionButton} ${styles.convertButton}`}
                >
                                  <FiCreditCard size={16} />
                Convert to Invoice
                </button>
              )}
              
              {quote.status === 'draft' && (
                <button 
                  onClick={handleSendQuote}
                  disabled={isLoading}
                  className={`${styles.actionButton} ${styles.sendButton}`}
                >
                  <FiMail size={16} />
                  Send Quote
                </button>
              )}
            </div>
          </div>

          {/* Quote Summary */}
          <div className={styles.sidebarCard}>
            <h3>Quote Summary</h3>
            <div className={styles.summaryItem}>
              <span>Status:</span>
              <span>{getStatusBadge(quote.status)}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Created:</span>
              <span>{format(quote.createdAt, 'MMM dd, yyyy')}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Created By:</span>
              <span>{quote.createdBy}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Items:</span>
              <span>{quote.items.length}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Total Amount:</span>
                              <span className={styles.totalAmount}>{formatCurrencySimple(quote.total)}</span>
            </div>
          </div>

          {/* Client Information */}
          <div className={styles.sidebarCard}>
            <h3>Client Information</h3>
            <div className={styles.clientSummary}>
              <h4>{quote.client.name}</h4>
              <p>{quote.client.email}</p>
              {quote.client.phone && <p>{quote.client.phone}</p>}
              <Link to={`/clients/${quote.client.id}`} className={styles.viewClientButton}>
                <FiUser size={16} />
                View Client
              </Link>
            </div>
          </div>

          {/* Timeline */}
          <div className={styles.sidebarCard}>
            <h3>Timeline</h3>
            <div className={styles.timeline}>
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Quote Created</strong>
                  <p>{format(quote.createdAt, 'MMM dd, yyyy')}</p>
                </div>
              </div>
              
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Quote Sent</strong>
                  <p>{format(quote.issueDate, 'MMM dd, yyyy')}</p>
                </div>
              </div>
              
              {quote.acceptedDate && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineDot} />
                  <div className={styles.timelineContent}>
                    <strong>Quote Accepted</strong>
                    <p>{format(quote.acceptedDate, 'MMM dd, yyyy')}</p>
                  </div>
                </div>
              )}
              
              <div className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <strong>Valid Until</strong>
                  <p>{format(quote.validUntil, 'MMM dd, yyyy')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Convert Quote Modal */}
      <ConvertQuoteModal
        quote={quote}
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onConvert={handleConvertQuote}
      />
    </motion.div>
  );
}
